import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase, isSupabaseConfigured } from "@/lib/supabase";
import { cacheKeys } from "@/lib/cache";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { LoadingSkeleton } from "@/components/LoadingSkeleton";
import { SEOHead } from "@/components/SEOHead";
import { 
  Search, 
  Filter, 
  Grid, 
  List, 
  SlidersHorizontal, 
  Star,
  ShoppingCart,
  Store,
  ChevronRight,
  Menu
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  image_url?: string;
  category_slug: string;
  shop_name?: string;
  shop_id?: string;
  rating?: number;
  reviews_count?: number;
  in_stock: boolean;
}

interface Category {
  id: string;
  name: string;
  slug: string;
  product_count?: number;
}

const Products = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  const [sortBy, setSortBy] = useState('relevance');

  // Fetch categories
  const { data: categories, isLoading: categoriesLoading } = useQuery({
    queryKey: cacheKeys.categories,
    queryFn: async () => {
      if (!isSupabaseConfigured) return [];
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .order('name');
      if (error) throw error;
      return data as Category[];
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
  });

  // Fetch products with filters
  const { data: products, isLoading: productsLoading, error } = useQuery({
    queryKey: cacheKeys.productsSearch(searchQuery, { 
      category: selectedCategory, 
      priceMin: priceRange.min, 
      priceMax: priceRange.max,
      sortBy 
    }),
    queryFn: async () => {
      if (!isSupabaseConfigured) {
        // Mock data when Supabase is not configured
        return [
          {
            id: '1',
            name: 'Premium Wireless Headphones',
            description: 'High-quality wireless headphones with noise cancellation',
            price: 299.99,
            image_url: 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=300&h=300&fit=crop',
            category_slug: 'electronics',
            shop_name: 'Tech Store',
            shop_id: '1',
            rating: 4.5,
            reviews_count: 128,
            in_stock: true,
          },
          {
            id: '2',
            name: 'Designer Leather Jacket',
            description: 'Premium leather jacket for fashion-forward individuals',
            price: 199.99,
            image_url: 'https://images.unsplash.com/photo-1551028719-00167b16eac5?w=300&h=300&fit=crop',
            category_slug: 'fashion',
            shop_name: 'Fashion Hub',
            shop_id: '2',
            rating: 4.8,
            reviews_count: 89,
            in_stock: true,
          }
        ] as Product[];
      }

      let query = supabase
        .from('products')
        .select(`
          *,
          shops(name),
          categories(name, slug)
        `)
        .eq('status', 'active');

      // Apply filters
      if (selectedCategory && selectedCategory !== 'all') {
        query = query.eq('category_slug', selectedCategory);
      }

      if (searchQuery.trim()) {
        query = query.or(`name.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%`);
      }

      if (priceRange.min) {
        query = query.gte('price', parseFloat(priceRange.min));
      }

      if (priceRange.max) {
        query = query.lte('price', parseFloat(priceRange.max));
      }

      // Apply sorting
      switch (sortBy) {
        case 'price_low':
          query = query.order('price', { ascending: true });
          break;
        case 'price_high':
          query = query.order('price', { ascending: false });
          break;
        case 'rating':
          query = query.order('rating', { ascending: false });
          break;
        case 'newest':
          query = query.order('created_at', { ascending: false });
          break;
        default:
          query = query.order('name');
      }

      const { data, error } = await query.limit(50);
      if (error) throw error;
      return data?.map(product => ({
        ...product,
        shop_name: product.shops?.name,
      })) as Product[];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (selectedCategory !== 'all') params.set('category', selectedCategory);
    setSearchParams(params);
  }, [searchQuery, selectedCategory, setSearchParams]);

  const handleAddToCart = (productId: string) => {
    if (!user) {
      toast.error("Please sign in to add items to cart");
      return;
    }
    toast.success("Added to cart!");
  };

  const CategorySidebar = ({ className = "" }: { className?: string }) => (
    <div className={`space-y-6 ${className}`}>
      <div>
        <h3 className="font-semibold mb-3">Categories</h3>
        <div className="space-y-1">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
              selectedCategory === 'all' 
                ? 'bg-primary text-primary-foreground' 
                : 'hover:bg-muted'
            }`}
          >
            All Categories
          </button>
          {categories?.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.slug)}
              className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-center justify-between ${
                selectedCategory === category.slug 
                  ? 'bg-primary text-primary-foreground' 
                  : 'hover:bg-muted'
              }`}
            >
              <span>{category.name}</span>
              {category.product_count && (
                <Badge variant="outline" className="text-xs">
                  {category.product_count}
                </Badge>
              )}
            </button>
          ))}
        </div>
      </div>

      <Separator />

      <div>
        <h3 className="font-semibold mb-3">Price Range</h3>
        <div className="space-y-3">
          <div className="flex gap-2">
            <Input
              type="number"
              placeholder="Min"
              value={priceRange.min}
              onChange={(e) => setPriceRange(prev => ({ ...prev, min: e.target.value }))}
              className="text-sm"
            />
            <Input
              type="number"
              placeholder="Max"
              value={priceRange.max}
              onChange={(e) => setPriceRange(prev => ({ ...prev, max: e.target.value }))}
              className="text-sm"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPriceRange({ min: '', max: '' })}
            className="w-full text-xs"
          >
            Clear Price Filter
          </Button>
        </div>
      </div>

      <Separator />

      <div>
        <h3 className="font-semibold mb-3">Sort By</h3>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="w-full p-2 border rounded-md text-sm bg-background"
        >
          <option value="relevance">Relevance</option>
          <option value="price_low">Price: Low to High</option>
          <option value="price_high">Price: High to Low</option>
          <option value="rating">Customer Rating</option>
          <option value="newest">Newest First</option>
        </select>
      </div>
    </div>
  );

  const ProductCard = ({ product }: { product: Product }) => (
    <Card className="group hover:shadow-lg transition-shadow">
      <CardContent className="p-4">
        <div className="aspect-square mb-4 bg-muted rounded-lg overflow-hidden">
          {product.image_url ? (
            <img
              src={product.image_url}
              alt={product.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground">
              No Image
            </div>
          )}
        </div>
        <div className="space-y-2">
          <Link 
            to={`/products/${product.id}`}
            className="font-medium text-sm hover:underline line-clamp-2"
          >
            {product.name}
          </Link>
          {product.shop_name && (
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Store className="h-3 w-3" />
              {product.shop_name}
            </p>
          )}
          {product.rating && (
            <div className="flex items-center gap-1">
              <div className="flex items-center">
                <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                <span className="text-xs font-medium ml-1">{product.rating}</span>
              </div>
              {product.reviews_count && (
                <span className="text-xs text-muted-foreground">
                  ({product.reviews_count})
                </span>
              )}
            </div>
          )}
          <div className="flex items-center justify-between">
            <div className="font-bold text-lg">
              ${product.price}
            </div>
            <Button 
              size="sm" 
              onClick={() => handleAddToCart(product.id)}
              disabled={!product.in_stock}
              className="text-xs"
            >
              <ShoppingCart className="h-3 w-3 mr-1" />
              {product.in_stock ? 'Add to Cart' : 'Out of Stock'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Error Loading Products</h1>
            <p className="text-muted-foreground">Please try again later.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title={`Products - Bahama Marketplace${selectedCategory !== 'all' ? ` | ${selectedCategory}` : ''}${searchQuery ? ` | Search: ${searchQuery}` : ''}`}
        description="Browse thousands of unique products from local sellers across the Bahamas. Find great deals and support local businesses."
      />
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-6">
          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <h1 className="text-2xl font-bold">Products</h1>
            <Badge variant="outline">
              {products?.length || 0} results
            </Badge>
            {selectedCategory !== 'all' && (
              <Badge>
                {categories?.find(c => c.slug === selectedCategory)?.name}
              </Badge>
            )}
          </div>

          {/* Search and View Controls */}
          <div className="flex flex-col lg:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex items-center gap-2">
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline" className="lg:hidden">
                    <Filter className="h-4 w-4 mr-2" />
                    Filters
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-80">
                  <SheetHeader>
                    <SheetTitle>Filters</SheetTitle>
                  </SheetHeader>
                  <div className="py-6">
                    <CategorySidebar />
                  </div>
                </SheetContent>
              </Sheet>

              <div className="hidden md:flex items-center gap-1 border rounded-md">
                <Button
                  variant={viewMode === 'grid' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('grid')}
                >
                  <Grid className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex gap-6">
            {/* Sidebar - Desktop */}
            <div className="hidden lg:block w-64 flex-shrink-0">
              <div className="sticky top-6">
                {categoriesLoading ? (
                  <div className="space-y-4">
                    <LoadingSkeleton className="h-6 w-32" />
                    <LoadingSkeleton className="h-8 w-full" />
                    <LoadingSkeleton className="h-8 w-full" />
                    <LoadingSkeleton className="h-8 w-full" />
                  </div>
                ) : (
                  <CategorySidebar />
                )}
              </div>
            </div>

            {/* Products Grid */}
            <div className="flex-1">
              {productsLoading ? (
                <div className={`grid ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' : 'grid-cols-1'} gap-6`}>
                  {Array.from({ length: 12 }).map((_, i) => (
                    <Card key={i}>
                      <CardContent className="p-4">
                        <LoadingSkeleton className="aspect-square mb-4 rounded-lg" />
                        <LoadingSkeleton className="h-4 w-3/4 mb-2" />
                        <LoadingSkeleton className="h-3 w-1/2 mb-2" />
                        <LoadingSkeleton className="h-6 w-1/3" />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : products?.length ? (
                <div className={`grid ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' : 'grid-cols-1'} gap-6`}>
                  {products.map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">🔍</div>
                  <h3 className="text-lg font-medium mb-2">No products found</h3>
                  <p className="text-muted-foreground mb-4">
                    Try adjusting your search or filters to find what you're looking for.
                  </p>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setSearchQuery('');
                      setSelectedCategory('all');
                      setPriceRange({ min: '', max: '' });
                      setSortBy('relevance');
                    }}
                  >
                    Clear All Filters
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Products;